// Bundled score examples: small SYNTHETIC MusicXML documents, one per
// engraving feature, loadable from the Score page's Examples dropdown so
// every notation capability can be exercised from the live site. They mirror
// the conventions of the (copyrighted, out-of-repo) reference scores — see
// CLAUDE.md's reference-scores milestone — without shipping a note of them.
// scoreExamples.test.ts proves every entry imports, validates and engraves.

import GUIDE_TONE_STROLL from './guide-tone-stroll.musicxml?raw';

export interface ScoreExample {
  id: string;
  label: string;
  xml: string;
}

const doc = (title: string, body: string) => `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="4.0">
  <movement-title>${title}</movement-title>
  <part-list><score-part id="P1"><part-name>Lead</part-name></score-part></part-list>
  <part id="P1">${body}</part>
</score-partwise>`;

const TWO_VOICES = doc(
  'Two Voices',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>1</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <harmony><root><root-step>G</root-step></root><kind>major-seventh</kind></harmony>
    <note><pitch><step>B</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>D</step><octave>5</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <backup><duration>16</duration></backup>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>2</voice><type>half</type></note>
    <note><pitch><step>F</step><alter>1</alter><octave>4</octave></pitch><duration>8</duration><voice>2</voice><type>half</type></note>
  </measure>
  <measure number="2">
    <harmony><root><root-step>C</root-step></root><kind>major-seventh</kind></harmony>
    <note><pitch><step>E</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>D</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>B</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <backup><duration>16</duration></backup>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>4</duration><voice>2</voice><type>quarter</type></note>
    <note><pitch><step>E</step><octave>4</octave></pitch><duration>4</duration><voice>2</voice><type>quarter</type></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>2</voice><type>half</type></note>
  </measure>`,
);

const CHORD_STACKS = doc(
  'Chords on One Stem',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>0</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <harmony><root><root-step>C</root-step></root><kind>major-seventh</kind></harmony>
    <note><pitch><step>C</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>E</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><pitch><step>D</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><chord/><pitch><step>E</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><chord/><pitch><step>B</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
  </measure>
  <measure number="2">
    <harmony><root><root-step>D</root-step></root><kind>dominant</kind></harmony>
    <note><pitch><step>E</step><alter>-1</alter><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>C</step><octave>5</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><pitch><step>D</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>F</step><alter>1</alter><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><chord/><pitch><step>A</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
  </measure>`,
);

const tripletNote = (step: string, octave: number) => `
    <note><pitch><step>${step}</step><octave>${octave}</octave></pitch><duration>4</duration><voice>1</voice><type>eighth</type>
      <time-modification><actual-notes>3</actual-notes><normal-notes>2</normal-notes></time-modification></note>`;
const sextupletNote = (step: string, octave: number) => `
    <note><pitch><step>${step}</step><octave>${octave}</octave></pitch><duration>2</duration><voice>1</voice><type>16th</type>
      <time-modification><actual-notes>6</actual-notes><normal-notes>4</normal-notes></time-modification></note>`;

const TUPLETS = doc(
  'Triplets and Sextuplets',
  `
  <measure number="1">
    <attributes>
      <divisions>12</divisions>
      <key><fifths>-1</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    ${tripletNote('F', 4)}${tripletNote('G', 4)}${tripletNote('A', 4)}
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>12</duration><voice>1</voice><type>quarter</type></note>
    ${tripletNote('B', 4)}${tripletNote('A', 4)}${tripletNote('G', 4)}
    <note><pitch><step>F</step><octave>4</octave></pitch><duration>12</duration><voice>1</voice><type>quarter</type></note>
  </measure>
  <measure number="2">
    ${sextupletNote('F', 4)}${sextupletNote('G', 4)}${sextupletNote('A', 4)}${sextupletNote('B', 4)}${sextupletNote('C', 5)}${sextupletNote('D', 5)}
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>12</duration><voice>1</voice><type>quarter</type></note>
    <note><pitch><step>A</step><octave>4</octave></pitch><duration>24</duration><voice>1</voice><type>half</type></note>
  </measure>`,
);

const GRACES_ORNAMENTS = doc(
  'Graces and Ornaments',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>0</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <note><grace slash="yes"/><pitch><step>D</step><octave>5</octave></pitch><voice>1</voice><type>eighth</type></note>
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><ornaments><trill-mark/></ornaments></notations></note>
    <note><pitch><step>B</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><ornaments><turn/></ornaments></notations></note>
    <note><pitch><step>A</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><ornaments><mordent/></ornaments></notations></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><ornaments><inverted-mordent/></ornaments></notations></note>
  </measure>
  <measure number="2">
    <note><grace/><pitch><step>A</step><octave>4</octave></pitch><voice>1</voice><type>16th</type></note>
    <note><grace/><pitch><step>B</step><octave>4</octave></pitch><voice>1</voice><type>16th</type></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>12</duration><voice>1</voice><type>half</type><dot/></note>
    <note><rest/><duration>4</duration><voice>1</voice><type>quarter</type></note>
  </measure>`,
);

const EXPRESSION = doc(
  'Slurs, Dynamics and Hairpins',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>-2</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <direction directive="yes"><direction-type><words>Cantabile</words></direction-type><sound tempo="88"/></direction>
    <direction><direction-type><dynamics><p/></dynamics></direction-type></direction>
    <note><pitch><step>E</step><alter>-1</alter><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type>
      <notations><slur type="start" number="1" placement="above"/></notations></note>
    <note><pitch><step>F</step><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><pitch><step>A</step><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><pitch><step>B</step><alter>-1</alter><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><slur type="stop" number="1"/></notations></note>
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><articulations><staccato/></articulations></notations></note>
  </measure>
  <measure number="2">
    <direction><direction-type><wedge type="crescendo"/></direction-type></direction>
    <note><pitch><step>D</step><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><articulations><accent/></articulations></notations></note>
    <note><pitch><step>E</step><alter>-1</alter><octave>5</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type>
      <notations><articulations><tenuto/></articulations></notations></note>
    <direction><direction-type><wedge type="stop"/></direction-type></direction>
    <direction><direction-type><dynamics><f/></dynamics></direction-type></direction>
    <note><pitch><step>F</step><octave>5</octave></pitch><duration>8</duration><voice>1</voice><type>half</type>
      <notations><articulations><strong-accent/></articulations></notations></note>
  </measure>`,
);

const REPEATS_VOLTAS = doc(
  'Repeats and Voltas',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>0</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <barline location="left"><bar-style>heavy-light</bar-style><repeat direction="forward"/></barline>
    <harmony><root><root-step>C</root-step></root><kind>major-seventh</kind></harmony>
    <note><pitch><step>E</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
  </measure>
  <measure number="2">
    <barline location="left"><ending number="1" type="start"/></barline>
    <harmony><root><root-step>D</root-step></root><kind>minor-seventh</kind></harmony>
    <harmony><root><root-step>G</root-step></root><kind>dominant</kind></harmony>
    <note><pitch><step>F</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><pitch><step>B</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <barline location="right"><ending number="1" type="stop"/><repeat direction="backward"/></barline>
  </measure>
  <measure number="3">
    <barline location="left"><ending number="2" type="start"/></barline>
    <harmony><root><root-step>C</root-step></root><kind>major-seventh</kind></harmony>
    <note><pitch><step>C</step><octave>5</octave></pitch><duration>16</duration><voice>1</voice><type>whole</type></note>
    <barline location="right"><bar-style>light-heavy</bar-style><ending number="2" type="discontinue"/></barline>
  </measure>`,
);

const ALTO_CLEF = `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="4.0">
  <movement-title>Viola Line</movement-title>
  <part-list><score-part id="P1"><part-name>Viola</part-name></score-part></part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key><fifths>2</fifths></key>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>C</sign><line>3</line></clef>
      </attributes>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>D</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>E</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
    <measure number="2">
      <note><pitch><step>G</step><octave>3</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>A</step><octave>3</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>D</step><octave>3</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
  </part>
</score-partwise>`;

// Clarinet in A: sounding = written − m3 (<chromatic>-3</chromatic>). The
// part is WRITTEN in Eb major so it SOUNDS in concert C major — the model
// stores the concert truth and derives this written view back.
const CLARINET_IN_A = `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="4.0">
  <movement-title>Clarinet in A</movement-title>
  <part-list><score-part id="P1"><part-name>Clarinet in A</part-name></score-part></part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key><fifths>-3</fifths></key>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>G</sign><line>2</line></clef>
        <transpose><diatonic>-2</diatonic><chromatic>-3</chromatic></transpose>
      </attributes>
      <note><pitch><step>E</step><alter>-1</alter><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>F</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
    <measure number="2">
      <note><pitch><step>B</step><alter>-1</alter><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>A</step><alter>-1</alter><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>E</step><alter>-1</alter><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
  </part>
</score-partwise>`;

const CUE_NOTES = doc(
  'Cue Notes',
  `
  <measure number="1">
    <attributes>
      <divisions>4</divisions>
      <key><fifths>2</fifths></key>
      <time><beats>4</beats><beat-type>4</beat-type></time>
      <clef><sign>G</sign><line>2</line></clef>
    </attributes>
    <note><pitch><step>A</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    <note><pitch><step>B</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
  </measure>
  <measure number="2">
    <note><cue/><pitch><step>D</step><octave>5</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><cue/><pitch><step>C</step><alter>1</alter><octave>5</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><cue/><pitch><step>B</step><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><cue/><pitch><step>A</step><octave>4</octave></pitch><duration>2</duration><voice>1</voice><type>eighth</type></note>
    <note><cue/><rest/><duration>4</duration><voice>1</voice><type>quarter</type></note>
    <note><cue/><pitch><step>F</step><alter>1</alter><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
  </measure>`,
);

const ENSEMBLE = `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="4.0">
  <movement-title>Duo</movement-title>
  <part-list>
    <score-part id="P1"><part-name>Voice</part-name></score-part>
    <score-part id="P2"><part-name>Ensemble</part-name></score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key><fifths>0</fifths></key>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>G</sign><line>2</line></clef>
      </attributes>
      <note><pitch><step>G</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>A</step><octave>4</octave></pitch><duration>4</duration><voice>1</voice><type>quarter</type></note>
      <note><pitch><step>B</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
    <measure number="2">
      <note><pitch><step>C</step><octave>5</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
      <note><pitch><step>G</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
    </measure>
  </part>
  <part id="P2">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key><fifths>0</fifths></key>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>G</sign><line>2</line></clef>
      </attributes>
      <note><pitch><step>E</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
      <note><pitch><step>F</step><octave>4</octave></pitch><duration>8</duration><voice>1</voice><type>half</type></note>
      <backup><duration>16</duration></backup>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>16</duration><voice>2</voice><type>whole</type></note>
    </measure>
    <measure number="2">
      <note><pitch><step>E</step><octave>4</octave></pitch><duration>16</duration><voice>1</voice><type>whole</type></note>
      <backup><duration>16</duration></backup>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>8</duration><voice>2</voice><type>half</type></note>
      <note><pitch><step>B</step><octave>3</octave></pitch><duration>8</duration><voice>2</voice><type>half</type></note>
    </measure>
  </part>
</score-partwise>`;

/** Order is the dropdown order: the fuller demos first, features after. */
export const SCORE_EXAMPLES: ScoreExample[] = [
  { id: 'piano-demo', label: 'Piano grand staff (Guide Tone Stroll)', xml: GUIDE_TONE_STROLL },
  { id: 'ensemble', label: 'Two parts (part picker)', xml: ENSEMBLE },
  { id: 'two-voices', label: 'Two voices — opposed stems', xml: TWO_VOICES },
  { id: 'chords', label: 'Chords on one stem', xml: CHORD_STACKS },
  { id: 'tuplets', label: 'Triplets & sextuplets', xml: TUPLETS },
  { id: 'graces', label: 'Grace notes & ornaments', xml: GRACES_ORNAMENTS },
  { id: 'expression', label: 'Slurs, dynamics & hairpins', xml: EXPRESSION },
  { id: 'repeats', label: 'Repeats & voltas', xml: REPEATS_VOLTAS },
  { id: 'cues', label: 'Cue notes', xml: CUE_NOTES },
  { id: 'alto-clef', label: 'Alto clef (viola)', xml: ALTO_CLEF },
  { id: 'clarinet-a', label: 'Transposing part (Clarinet in A)', xml: CLARINET_IN_A },
];

/** Reference samples served from public/samples/ — fetched on demand (they
 *  are ~150-250 KB each, too big to inline in the bundle). */
export interface BundledSample {
  id: string;
  label: string;
  file: string;
}

export const BUNDLED_SAMPLES: BundledSample[] = [
  { id: 'telemann', label: 'Telemann — Liebe! Liebe! (full score, 3/8)', file: 'Telemann.musicxml' },
  { id: 'ave-maria', label: 'Schubert — Ave Maria (voice + piano, lyrics)', file: 'SchbAvMaSample.musicxml' },
];
